import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export const OR_LOGO = "/openring/logo.png";
export const OR_ARENA = "/openring/arena.jpg";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

export const ApplyButton = ({ label, testid }) => (
  <Link
    to="/openring/apply"
    data-testid={testid}
    className="group inline-flex items-center gap-3 bg-brand hover:bg-brand-hi text-black font-display text-2xl md:text-3xl uppercase tracking-wide px-8 py-4 transition-colors"
  >
    {label}
    <ArrowRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

const Hero = () => (
  <section data-testid="openring-hero" className="relative bg-black">
    <h1 className="sr-only">OPEN RING</h1>
    <div className="relative w-full aspect-[16/9] max-h-[100vh] overflow-hidden">
      <img src={OR_ARENA} alt="OPEN RING" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black via-black/60 to-transparent" />
      <div className="hidden md:flex absolute inset-x-0 bottom-[7%] flex-col items-center gap-6">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
          data-testid="openring-tagline" className="font-display text-4xl lg:text-6xl tracking-[0.15em] text-white">
          THE RING IS <span className="text-brand">YOURS.</span>
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }}>
          <ApplyButton label="Apply for Open Ring" testid="openring-hero-apply-btn" />
        </motion.div>
      </div>
    </div>
    <div className="md:hidden flex flex-col items-center gap-5 px-6 pt-4 pb-12">
      <p data-testid="openring-tagline-mobile" className="font-display text-4xl tracking-[0.12em] text-white text-center">
        THE RING IS <span className="text-brand">YOURS.</span>
      </p>
      <ApplyButton label="Apply for Open Ring" testid="openring-hero-apply-btn-mobile" />
    </div>
  </section>
);

const About = () => (
  <section data-testid="openring-about" className="px-6 md:px-12 lg:px-20 py-20 md:py-32 border-t border-white/10">
    <motion.div {...fade} className="max-w-4xl">
      <img src={OR_LOGO} alt="OPEN RING" className="h-12 md:h-16 w-auto mb-10" />
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-[2px] bg-brand" />
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">About Open Ring</span>
      </div>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none mb-10">
        A different kind of professional wrestling showcase.
      </h2>
      <div className="space-y-6 text-zinc-300 text-base md:text-lg leading-relaxed">
        <p>OPEN RING is a professional wrestling showcase presented by Championship Wrestling Federation, giving professional wrestlers an opportunity to step into the spotlight and showcase their abilities in front of a live audience.</p>
        <p>From rising talent to established veterans, OPEN RING brings together performers from across the independent wrestling scene for exciting matches, fresh opportunities, and unexpected encounters.</p>
        <p>Every event brings different talent, different matchups, and another opportunity to make an impression.</p>
      </div>
      <p className="font-display text-3xl md:text-4xl tracking-[0.12em] mt-10">THE RING IS <span className="text-brand">YOURS.</span></p>
    </motion.div>
  </section>
);

const Talent = () => (
  <section data-testid="openring-talent" className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
    <motion.div {...fade} className="relative border border-white/10 bg-[#0c0c0c] p-8 md:p-14 overflow-hidden">
      <span className="absolute left-0 top-0 h-full w-1 bg-brand" />
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none mb-8">
        Want to step into the <span className="text-brand">ring?</span>
      </h2>
      <div className="max-w-3xl space-y-5 text-zinc-300 text-base md:text-lg leading-relaxed">
        <p>Are you a professional wrestler looking for an opportunity to showcase your abilities?</p>
        <p>CWF is accepting talent submissions for consideration for upcoming OPEN RING events.</p>
        <p>Whether you're an established competitor or an emerging name on the independent wrestling scene, we want to hear from you.</p>
        <p>All applications are reviewed by CWF management, and selected wrestlers will be contacted regarding potential bookings.</p>
      </div>
      <div className="mt-10">
        <ApplyButton label="Submit Your Application" testid="openring-submit-application-btn" />
      </div>
    </motion.div>
  </section>
);

export default function OpenRing() {
  return (
    <div data-testid="openring-page">
      <Hero />
      <About />
      <Talent />
    </div>
  );
}
